import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import React, { lazy, Suspense, useEffect } from "react";import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import { initAnalytics, track, EVENTS } from "./lib/analytics";
import { isAdminEmail } from "./utils/admin";
import "./App.css";

// Initialize analytics once at module-load
if (typeof window !== "undefined") {
  initAnalytics();
}

// Code-split: lazy-load all non-critical route pages
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AnalyzingPage = lazy(() => import("./pages/AnalyzingPage"));
const PropertyReviewPage = lazy(() => import("./pages/PropertyReviewPage"));
const CampaignBuilderPage = lazy(() => import("./pages/CampaignBuilderPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminTestersPage = lazy(() => import("./pages/AdminTestersPage"));
const AdminFoundingPage = lazy(() => import("./pages/AdminFoundingPage"));
const AdminReferralsPage = lazy(() => import("./pages/AdminReferralsPage"));
const ReferralsPage = lazy(() => import("./pages/ReferralsPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const BrandingPage = lazy(() => import("./pages/BrandingPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const DivaVideoPage = lazy(() => import("./pages/DivaVideoPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const MasterViewPage = lazy(() => import("./pages/MasterViewPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const DataDeletionPage = lazy(() => import("./pages/DataDeletionPage"));
const FoundingPage = lazy(() => import("./pages/FoundingPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AudiencePreviewPage = lazy(() => import("./pages/AudiencePreviewPage"));
import { FoundingBanner } from "./components/marketing/FoundingBanner";
const VapiConfigPage = lazy(() => import("./pages/VapiConfigPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

// Accessibility: patch any third-party injected images missing alt attributes
if (typeof window !== 'undefined') {
  const observer = new MutationObserver(() => {
    document.querySelectorAll('img:not([alt])').forEach(img => {
      img.setAttribute('alt', '');
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-6">We're sorry, but something unexpected happened. Please try refreshing the page.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#39FF14] text-black font-semibold px-6 py-3 rounded-lg hover:bg-[#32E012] transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { user, loading, hasActiveSubscription } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  // Whitelisted admins bypass subscription check (SEC-1: no domain-based bypass)
  const isAdmin = isAdminEmail(user.email);
  
  // Enforce subscription check for protected routes (skip for admins)
  if (requireSubscription && !isAdmin && !hasActiveSubscription()) {
    return <Navigate to="/pricing" replace />;
  }
  
  return children;
};

// Meta OAuth callback redirect — captures ?code= from Facebook and passes to dashboard
const MetaCallbackRedirect = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  return <Navigate to={`/dashboard?code=${code || ''}&onboarding=complete`} replace />;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Track route change for funnel analytics
    if (pathname === "/") track(EVENTS.LANDING_VIEWED, { path: pathname });
    else if (pathname === "/pricing") track(EVENTS.PRICING_PAGE_VIEWED, { path: pathname });
    else if (pathname === "/auth") track(EVENTS.SIGNUP_PAGE_VIEWED, { path: pathname });
    else if (pathname === "/dashboard") track(EVENTS.DASHBOARD_VIEWED, { path: pathname });
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={null}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/checkout/success" element={
        <ProtectedRoute requireSubscription={false}><CheckoutSuccessPage /></ProtectedRoute>
      } />
      <Route path="/meta-callback" element={<MetaCallbackRedirect />} />
      <Route path="/analyzing" element={
        <ProtectedRoute><AnalyzingPage /></ProtectedRoute>
      } />
      <Route path="/property/:propertyId" element={
        <ProtectedRoute><PropertyReviewPage /></ProtectedRoute>
      } />
      <Route path="/campaign/:propertyId" element={
        <ProtectedRoute><CampaignBuilderPage /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute><BillingPage /></ProtectedRoute>
      } />
      <Route path="/branding" element={
        <ProtectedRoute><BrandingPage /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><SettingsPage /></ProtectedRoute>
      } />
      <Route path="/integrations" element={
        <ProtectedRoute><IntegrationsPage /></ProtectedRoute>
      } />
      <Route path="/videos" element={
        <ProtectedRoute><DivaVideoPage /></ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute><TeamPage /></ProtectedRoute>
      } />
      <Route path="/master-view" element={
        <ProtectedRoute><MasterViewPage /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireSubscription={false}><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/testers" element={
        <ProtectedRoute requireSubscription={false}><AdminTestersPage /></ProtectedRoute>
      } />
      <Route path="/admin/founding" element={
        <ProtectedRoute requireSubscription={false}><AdminFoundingPage /></ProtectedRoute>
      } />
      <Route path="/admin/referrals" element={
        <ProtectedRoute requireSubscription={false}><AdminReferralsPage /></ProtectedRoute>
      } />
      <Route path="/referrals" element={
        <ProtectedRoute requireSubscription={false}><ReferralsPage /></ProtectedRoute>
      } />
      <Route path="/voice-config" element={
        <ProtectedRoute><VapiConfigPage /></ProtectedRoute>
      } />
      {/* Public Pages */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/founding" element={<FoundingPage />} />
      <Route path="/audience-preview" element={<AudiencePreviewPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <Helmet>
            <title>AdWup.ai | Autonomous AI Real Estate Marketing Platform</title>
            <meta name="description" content="AdWup.ai autonomously scrapes your property, builds 100s of ads, and finds qualified buyers while you sleep. The AI engine for 10x real estate leads." />
            <meta name="keywords" content="AI for real estate leads, real estate marketing automation, Meta ads for realtors, AI ad campaigns, property marketing AI" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "AdWup.ai",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web",
                "offers": [
                  {
                    "@type": "Offer",
                    "name": "Listing Pro",
                    "price": "149",
                    "priceCurrency": "USD",
                    "priceValidUntil": "2026-12-31"
                  },
                  {
                    "@type": "Offer", 
                    "name": "Market Dominator",
                    "price": "349",
                    "priceCurrency": "USD",
                    "priceValidUntil": "2026-12-31"
                  }
                ]
              })}
            </script>
          </Helmet>
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
            <Toaster 
              position="top-right" 
              duration={6000}
              richColors
              visibleToasts={5}
              offset="20px"
              toastOptions={{
                style: {
                  fontSize: '15px',
                  padding: '16px 20px',
                  fontWeight: '500',
                  minWidth: '360px',
                  maxWidth: '520px',
                  boxShadow: '0 10px 40px -10px rgba(0,0,0,0.4)',
                },
                className: 'adwup-toast',
                classNames: {
                  toast: 'adwup-toast',
                  error: 'adwup-toast-error',
                  success: 'adwup-toast-success',
                  warning: 'adwup-toast-warning',
                  info: 'adwup-toast-info',
                  title: 'adwup-toast-title',
                  description: 'adwup-toast-description',
                  closeButton: 'adwup-toast-close',
                },
              }}
              closeButton={true}
            />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
